import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { AutoAddRowsPlugin } from "@revolist/revogrid";
import SelectColumnType from "@revolist/revogrid-column-select";
import { forkJoin, Observable, of, Subscription } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import Swal from "sweetalert2";
import { AcademicCalendarService } from "../../../../shared/services/academic-calendar.service";
import { PathwayMappingService } from "../../../../shared/services/pathway-mapping.service";
import { SchoolAndLocationService } from "../../../../shared/services/school-and-location.service";
import { SchoolProgramService } from "../../../../shared/services/school-program.service";
import { ResponseService } from "../../../../shared/services/response-service.service";
import { TranslateService } from "../../../../shared/services/translate.service";
import {
  allowRevoGridFilterInputTyping,
  commitRevoGridEdit,
  createDateEditor,
  createMultiSelectEditor,
  createSingleSelectEditor,
  dateCellTemplate,
  firstEditableColumn,
  focusRevoGridRow,
  isEmptyValue,
  parseDateText,
  rowActionButton,
  seedNewRows,
  selectCellTemplate,
  syncRevoGridSource,
  withValidationStyles,
} from "../../../../shared/utils/revo-grid";

interface Option {
  label: string;
  value: any;
}

interface ActivityRow {
  school_program_activity_id?: number | null;
  academic_year: string | null;
  school_location_id: number | null;
  school_program_id: number | null;
  school_program_category_id: number | null;
  year_level_id: number[];
  activity_name: string;
  activity_description: string | null;
  name_on_report_card: string;
  gender: string | null;
  start_date: string | null;
  end_date: string | null;
  max_registration_date: string | null;
  schoolday: string[] | string;
  min_participant: number | null;
  max_participant: number | null;
  participation_fee: number | null;
  status: string;
  changed: boolean;
  deleted?: boolean;
}

const FILTER_KEYS = [
  "academic_year",
  "school_location_id",
  "school_level_id",
  "school_program_id",
  "school_program_category_id",
];
const GENDER_OPTIONS: Option[] = [
  { label: "All", value: null },
  { label: "Male", value: "M" },
  { label: "Female", value: "F" },
];
const STATUS_OPTIONS: Option[] = ["Active", "Inactive", "Hold"].map((s) => ({
  label: s,
  value: s,
}));
const DATE_PROPS = ["start_date", "end_date", "max_registration_date"];
const REQUIRED_PROPS = [
  "academic_year",
  "school_location_id",
  "school_program_id",
  "school_program_category_id",
  "year_level_id",
  "activity_name",
  ...DATE_PROPS,
  "min_participant",
  "max_participant",
  "status",
];
const MAX_ERROR_ROWS = 5;

const SCHOOL_DAYS = [
  ["Monday", "Senin"],
  ["Tuesday", "Selasa"],
  ["Wednesday", "Rabu"],
  ["Thursday", "Kamis"],
  ["Friday", "Jumat", "Jum'at"],
  ["Saturday", "Sabtu"],
  ["Sunday", "Minggu", "Ahad"],
];
const DAY_OPTIONS: Option[] = SCHOOL_DAYS.map(([day]) => ({ label: day, value: day }));

const dayOf = (text: string): string | undefined => {
  const token = text.trim().toLowerCase();
  if (token.length < 3) return undefined;
  const matches = SCHOOL_DAYS.filter((names) => names.some((name) => name.toLowerCase().startsWith(token)));
  return matches.length === 1 ? matches[0][0] : undefined;
};

const toDays = (value: any): string[] | string => {
  const tokens = (Array.isArray(value) ? value : String(value ?? "").split(/[,;\n]/))
    .map((token) => String(token).trim())
    .filter(Boolean);
  const days = tokens.map(dayOf);
  if (days.some((day) => !day)) return String(value);
  return DAY_OPTIONS.map((option) => option.value).filter((day) => days.includes(day));
};

const toIds = (value: any): number[] =>
  (Array.isArray(value) ? value : String(value ?? "").split(","))
    .map(Number)
    .filter(Boolean);

const optionLabel = (options: Option[], value: any): string =>
  options.find((option) => String(option.value ?? "") === String(value ?? ""))?.label ??
  String(value ?? "");

const toOptions = (response: any, valueKey: string, labelKey: string): Option[] =>
  (response?.result?.data ?? response?.data ?? []).map((item: any) => ({
    value: item[valueKey] ?? item,
    label: item[labelKey] ?? item,
  }));

@Component({
  selector: "app-activity-grid",
  templateUrl: "./activity-grid.component.html",
  styleUrls: ["./activity-grid.component.scss"],
})
export class ActivityGridComponent implements OnInit, OnDestroy {
  @Input() filters: any = {};
  @ViewChild("activityGrid", { read: ElementRef }) gridRef?: ElementRef<HTMLElement>;

  loading = true;
  saving = false;
  selectorVisible = true;
  source: ActivityRow[] = [];
  columns: any[] = [];
  columnTypes = { select: new SelectColumnType() };
  clipboardConfig = { rangeFill: true };
  gridPlugins = [AutoAddRowsPlugin];
  onGridKeydown = allowRevoGridFilterInputTyping;

  academicYearOptions: Option[] = [];
  locationOptions: Option[] = [];
  programOptions: Option[] = [];
  yearLevelOptions: Option[] = [];
  categoryOptions: Record<number, Option[]> = {};

  private loadSub?: Subscription;
  private invalidCells = new Map<ActivityRow, Set<string>>();

  constructor(
    public activeModal: NgbActiveModal,
    private activityService: SchoolProgramService,
    private academicService: AcademicCalendarService,
    private locationService: SchoolAndLocationService,
    private pathwayService: PathwayMappingService,
    private rpSer: ResponseService,
    private translate: TranslateService,
  ) {
    this.translate.load("activity", "extracurricular");
  }

  private get gridElement(): any {
    return this.gridRef?.nativeElement ?? null;
  }

  get hasSchoolLevel(): boolean {
    return !!(this.filters?.academic_year && this.filters?.school_location_id);
  }

  ngOnInit(): void {
    this.filters = { ...this.filters };
    this.loadSub = forkJoin({
      academicYears: this.safe(this.academicService.getAcademicYears()),
      locations: this.safe(this.locationService.getSchoolLocations()),
    }).subscribe(({ academicYears, locations }) => {
      this.academicYearOptions = toOptions(academicYears, "academic_year", "academic_year");
      this.locationOptions = toOptions(locations, "school_location_id", "school_short_address");
      this.buildColumns();
      this.loadActivities();
    });
  }
  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }

  onNewRows(event: Event): void {
    if (this.saving) {
      event.preventDefault();
      return;
    }
    seedNewRows(event as CustomEvent<{ newRows: { index: number; data: any }[] }>, () => this.createEmptyRow());
  }

  private loadActivities(): void {
    this.loadSub?.unsubscribe();
    this.source = [];
    this.invalidCells.clear();
    if (!this.hasSchoolLevel) return void (this.loading = false);
    this.loading = true;
    const { academic_year, school_location_id, school_level_id } = this.filters;
    this.loadSub = forkJoin({
      activities: this.safe(
        this.activityService.getSchoolProgramActivity({
          academic_year,
          school_location_id,
          school_program_id: this.filters.school_program_id || null,
          school_program_category_id: this.filters.school_program_category_id || null,
          approved: "all",
          size: 1000,
        }),
      ),
      programs: this.safe(
        this.activityService.getSchoolProgram({ academic_year, school_location_id }),
      ),
      yearLevels: this.safe(
        this.pathwayService.yearLevelNoAcademic({
          academic_year,
          school_location_id,
          school_level_id,
        }),
      ),
    })
      .pipe(
        switchMap(({ activities, programs, yearLevels }) => {
          if (!activities) this.rpSer.showAddError();
          this.programOptions = toOptions(programs, "school_program_id", "school_program_name");
          this.yearLevelOptions = (yearLevels?.data ?? []).map((item: any) => ({
            value: item.year_level_id,
            label: item.description ?? item.year_level_name,
          }));
          this.source = (activities?.status ? activities.data ?? [] : []).map(
            (item: any) => this.toRow(item),
          );
          return this.loadCategories([
            this.filters.school_program_id,
            ...this.source.map((row) => row.school_program_id),
          ]);
        }),
      )
      .subscribe(() => (this.loading = false));
  }

  private loadCategories(programIds: any[]): Observable<unknown> {
    const missing = [...new Set(programIds.map(Number))].filter(
      (id) => id && !this.categoryOptions[id],
    );
    if (!missing.length) return of(null);
    return forkJoin(
      missing.map((id) =>
        this.safe(this.activityService.getSchoolProgramCategory({ school_program_id: id })).pipe(
          map((response) => {
            this.categoryOptions[id] = toOptions(
              response,
              "school_program_category_id",
              "category_name",
            );
          }),
        ),
      ),
    );
  }

  private safe<T>(request: Observable<T>): Observable<any> {
    return request.pipe(catchError(() => of(null)));
  }

  onChangeAcademicYear(item: any): void {
    if (this.unchanged(item, "academic_year")) return;
    this.requestRefetch({
      academic_year: item?.academic_year ?? null,
      school_location_id: null,
      school_level_id: null,
      school_program_id: null,
      school_program_category_id: null,
    });
  }
  onChangeSchoolLevel(item: any): void {
    if (this.unchanged(item, "school_location_id", "school_level_id")) return;
    this.requestRefetch({
      school_location_id: item?.school_location_id ?? null,
      school_level_id: item?.school_level_id ?? null,
      school_program_id: null,
      school_program_category_id: null,
    });
  }
  onChangeSchoolProgram(item: any): void {
    if (this.unchanged(item, "school_program_id")) return;
    this.requestRefetch({
      school_program_id: item?.school_program_id ?? null,
      school_program_category_id: null,
    });
  }
  onChangeSchoolProgramCategory(item: any): void {
    this.requestRefetch({
      school_program_category_id: item?.school_program_category_id ?? null,
    });
  }

  private unchanged(item: any, ...keys: string[]): boolean {
    return keys.every(
      (key) => String(item?.[key] ?? "") === String(this.filters[key] ?? ""),
    );
  }

  private async requestRefetch(changes: any): Promise<void> {
    const next = { ...this.filters, ...changes };
    if (this.unchanged(next, ...FILTER_KEYS)) return;
    commitRevoGridEdit(this.gridElement);
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    if (this.source.some((row) => row.changed)) {
      const { isConfirmed } = await Swal.fire({
        title: "Discard unsaved changes?",
        text: "You have unsaved activity changes. Discard them and reload the grid?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Discard",
        cancelButtonText: "Cancel",
      });
      if (!isConfirmed) {
        this.selectorVisible = false;
        setTimeout(() => (this.selectorVisible = true));
        return;
      }
    }
    this.filters = next;
    this.loadActivities();
  }

  private buildColumns(): void {
    const text = { sortable: true, filter: "string" };
    const select = (
      prop: keyof ActivityRow,
      name: string,
      size: number,
      options: (model: any) => Option[],
      readonly = false,
    ) => ({
      prop,
      name,
      size,
      readonly,
      ...text,
      // type to search: options whose label starts with the typed text (any case), first match highlighted
      editor: createSingleSelectEditor({ source: (model) => options(model) }),
      cellTemplate: selectCellTemplate((model) => optionLabel(options(model), model[prop]), () => readonly, name),
    });
    const date = (prop: keyof ActivityRow, name: string, size: number) => ({
      prop,
      name,
      size,
      sortable: true,
      filter: "date",
      cellTemplate: dateCellTemplate(prop),
      editor: createDateEditor(),
    });
    const dataColumns = [
      select("academic_year", "Academic Year", 150, () => this.academicYearOptions, true),
      select("school_location_id", "School Location", 190, () => this.locationOptions, true),
      select("school_program_id", "School Program Name", 220, () => this.programOptions),
      select(
        "school_program_category_id",
        "Category Name",
        190,
        (model) => this.categoryOptions[model.school_program_id] ?? [],
      ),
      {
        prop: "year_level_id",
        name: "Year Level",
        size: 210,
        filter: "string",
        editor: createMultiSelectEditor({
          source: () => this.yearLevelOptions,
          labelKey: "label",
          valueKey: "value",
        }),
        cellTemplate: selectCellTemplate(
          (model) =>
            this.yearLevelOptions
              .filter((option) => toIds(model.year_level_id).includes(+option.value))
              .map((option) => option.label)
              .join(", "),
          () => false,
          "Year Level",
        ),
      },
      { prop: "activity_name", name: "Activity Name", size: 210, ...text },
      { prop: "activity_description", name: "Activity Desc.", size: 240, ...text },
      { prop: "name_on_report_card", name: "Activity Name on Report Card", size: 260, ...text },
      select("gender", "Gender", 120, () => GENDER_OPTIONS),
      date("start_date", "Start Date", 145),
      date("end_date", "End Date", 145),
      date("max_registration_date", "Max Registration Date", 185),
      {
        prop: "schoolday",
        name: "School Day",
        size: 210,
        filter: "string",
        editor: createMultiSelectEditor({
          source: () => DAY_OPTIONS,
          labelKey: "label",
          valueKey: "value",
        }),
        cellTemplate: selectCellTemplate(
          (model) => (Array.isArray(model.schoolday) ? model.schoolday.join(", ") : String(model.schoolday ?? "")),
          () => false,
          "School Day",
        ),
      },
      { prop: "min_participant", name: "Minimum Participant", size: 175, columnType: "numeric" },
      { prop: "max_participant", name: "Maximum Participant", size: 175, columnType: "numeric" },
      {
        prop: "participation_fee",
        name: "Participant Fee",
        size: 150,
        columnType: "numeric",
        readonly: ({ model }: any) => model.school_program_activity_id != null,
      },
      select("status", "Status", 130, () => STATUS_OPTIONS),
    ];
    this.columns = [
      ...withValidationStyles(dataColumns, REQUIRED_PROPS, (model, prop) => !!this.invalidCells.get(model)?.has(prop)),
      {
        prop: "delete",
        name: "",
        size: 60,
        readonly: true,
        cellTemplate: (h: any, { model }: any) =>
          rowActionButton(h, model.deleted ? "undo" : "delete", model.deleted ? "Undo" : "Delete", () =>
            this.onDeleteRow(model),
          ),
      },
    ];
  }

  onAfterEdit(event: CustomEvent<any>): void {
    const detail = event.detail;
    const edits: [ActivityRow, string[]][] =
      detail?.models && detail?.data
        ? Object.entries(detail.data as Record<string, object>)
            .map(([index, values]) => [detail.models[index], Object.keys(values)] as [ActivityRow, string[]])
            .filter(([row]) => !!row)
        : detail?.model
          ? [[detail.model, [String(detail.prop)]]]
          : [];
    edits.forEach(([row, props]) => {
      row.changed = true;
      row.year_level_id = toIds(row.year_level_id);
      if (props.includes("schoolday")) row.schoolday = toDays(row.schoolday);
      props
        .filter((prop) => DATE_PROPS.includes(prop))
        .forEach((prop) => ((row as any)[prop] = parseDateText((row as any)[prop]) ?? ((row as any)[prop] || null)));
      if (props.includes("school_program_id") && !props.includes("school_program_category_id")) {
        row.school_program_category_id = null;
      }
    });
    const programRows = edits.filter(([, props]) => props.includes("school_program_id")).map(([row]) => row);
    if (programRows.length) {
      this.loadCategories(programRows.map((row) => row.school_program_id)).subscribe();
    }
    const invalidRows = edits.map(([row]) => row).filter((row) => this.invalidCells.has(row));
    if (invalidRows.length) {
      invalidRows.forEach((row) => {
        const props = this.invalidProps(row);
        if (props.length) this.invalidCells.set(row, new Set(props));
        else this.invalidCells.delete(row);
      });
      this.refreshGrid();
    }
  }

  async onAddRow(): Promise<void> {
    if (this.saving) return;
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    const row = this.createEmptyRow();
    this.source = [...this.source, row];
    await focusRevoGridRow(this.gridElement, row, firstEditableColumn(this.columns, row));
  }

  private createEmptyRow(): ActivityRow {
    return {
      school_program_activity_id: null,
      academic_year: this.filters.academic_year ?? null,
      school_location_id: this.filters.school_location_id ?? null,
      school_program_id: this.filters.school_program_id ?? null,
      school_program_category_id: this.filters.school_program_category_id ?? null,
      year_level_id: [],
      activity_name: "",
      activity_description: null,
      name_on_report_card: "",
      gender: null,
      start_date: null,
      end_date: null,
      max_registration_date: null,
      schoolday: [],
      min_participant: null,
      max_participant: null,
      participation_fee: null,
      status: "Active",
      changed: true,
    };
  }

  private toRow(item: any): ActivityRow {
    return {
      ...this.createEmptyRow(),
      ...item,
      school_location_id: +item.school_location_id || null,
      school_program_id: +item.school_program_id || null,
      school_program_category_id: +item.school_program_category_id || null,
      year_level_id: toIds(item.year_level_id),
      start_date: item.start_date?.slice(0, 10) ?? null,
      end_date: item.end_date?.slice(0, 10) ?? null,
      max_registration_date: item.max_registration_date?.slice(0, 10) ?? null,
      schoolday: toDays(item.schoolday ?? []),
      changed: false,
    };
  }

  async onDeleteRow(row: ActivityRow): Promise<void> {
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    this.invalidCells.delete(row);
    if (row.school_program_activity_id == null) {
      this.source = this.source.filter((item) => item !== row);
      return;
    }
    row.deleted = !row.deleted;
    row.changed = true;
    this.source = [...this.source];
  }

  async onSave(): Promise<void> {
    if (this.saving) return;
    commitRevoGridEdit(this.gridElement);
    this.source = await syncRevoGridSource(this.gridElement, this.source);
    const rows = this.source.filter((row) => row.deleted || this.isRowFilled(row));
    const activities = rows.map((row) => ({
      ...row,
      activity_name: row.activity_name?.trim(),
      name_on_report_card: row.name_on_report_card?.trim(),
      activity_description: row.activity_description?.trim() || null,
    }));
    if (!activities.length) {
      return void this.rpSer.showAddError("There is nothing to save.");
    }
    const active = activities.filter((row) => !row.deleted);
    const activeRows = rows.filter((row) => !row.deleted);
    this.markInvalidCells(activeRows);
    const missing = this.missingRequired(activeRows);
    if (missing) {
      return void this.rpSer.showAddError(`Complete the required fields. ${missing}`);
    }
    if (active.some((row) => DATE_PROPS.some((prop) => (row as any)[prop] && !parseDateText((row as any)[prop])))) {
      return void this.rpSer.showAddError("Dates must be valid (dd/mm/yyyy).");
    }
    if (active.some((row) => !Array.isArray(row.schoolday))) {
      return void this.rpSer.showAddError("School day must be Monday to Sunday.");
    }
    if (active.some((row) => row.start_date > row.end_date || row.max_registration_date > row.end_date)) {
      return void this.rpSer.showAddError(
        "Start date and max registration date can't be later than end date.",
      );
    }
    if (active.some((row) => [row.min_participant, row.max_participant, row.participation_fee].some((value) => +value < 0))) {
      return void this.rpSer.showAddError("Participant counts and participation fee can't be negative.");
    }
    this.saving = true;
    this.activityService.bulkSaveSchoolProgramActivity({ activities }).subscribe({
      next: (response: any) => {
        this.saving = false;
        if (!response?.status) return void this.rpSer.showAddError(response?.message);
        this.rpSer.showAddSuccess(response.message);
        this.activeModal.close(response.data);
      },
      error: () => {
        this.saving = false;
        this.rpSer.showAddError();
      },
    });
  }
  onClose(): void {
    this.activeModal.dismiss();
  }

  private isRowFilled(row: ActivityRow): boolean {
    return !!(
      row.activity_name ||
      row.name_on_report_card ||
      row.school_program_category_id ||
      row.start_date ||
      row.end_date
    );
  }
  private invalidProps(row: ActivityRow): string[] {
    const value = (prop: string) => (row as any)[prop];
    return [
      ...REQUIRED_PROPS.filter((prop) => isEmptyValue(value(prop))),
      ...DATE_PROPS.filter((prop) => !isEmptyValue(value(prop)) && !parseDateText(value(prop))),
      ...(Array.isArray(row.schoolday) ? [] : ["schoolday"]),
    ];
  }
  private missingRequired(rows: ActivityRow[]): string {
    const columnName = (prop: string) => this.columns.find((column) => column.prop === prop)?.name ?? prop;
    const lines = rows
      .map((row) => ({
        line: this.source.indexOf(row) + 1,
        props: REQUIRED_PROPS.filter((prop) => isEmptyValue((row as any)[prop])),
      }))
      .filter(({ props }) => props.length)
      .map(({ line, props }) => `Row ${line}: ${props.map(columnName).join(", ")}`);
    if (lines.length <= MAX_ERROR_ROWS) return lines.join("; ");
    return `${lines.slice(0, MAX_ERROR_ROWS).join("; ")}; and ${lines.length - MAX_ERROR_ROWS} more rows.`;
  }
  private markInvalidCells(rows: ActivityRow[]): void {
    this.invalidCells = new Map(
      rows
        .map((row) => [row, new Set(this.invalidProps(row))] as [ActivityRow, Set<string>])
        .filter(([, props]) => props.size),
    );
    this.refreshGrid();
  }
  private refreshGrid(): void {
    this.gridElement?.refresh?.("all")?.catch?.(() => undefined);
  }
}
